/**
 * Tidtaking / målgang på regattasiden.
 *
 * Arbeidsflyt:
 *  - Lista bygges på forhånd: legg til båter med seilnummer (kan gjøres før start
 *    og når som helst senere).
 *  - Når en båt går i mål trykkes radens «🏁 Mål»-knapp — den tildeler neste
 *    plass og henter tiden fra den synkroniserte klokka.
 *  - Alle felt (seilnummer, plass, tid) kan redigeres manuelt.
 *  - Redigering er åpen for alle påmeldte båter: du må være logget inn med
 *    båtnummer + båt-passord (fra «🔑 Logg inn med båt»), og båten påmeldt.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type FinishRecord } from '../lib/api.ts';
import { syncedNow } from '../lib/timesync.ts';

/** ms → "m:ss" eller "h:mm:ss" (negativt får ledende «-»). */
export function formatElapsed(ms: number | null): string {
  if (ms === null || ms === undefined) return '';
  const sign = ms < 0 ? '-' : '';
  const total = Math.floor(Math.abs(ms) / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, '0');
  if (h > 0) return `${sign}${h}:${String(m).padStart(2, '0')}:${ss}`;
  return `${sign}${m}:${ss}`;
}

/** "m:ss" / "h:mm:ss" / "+m:ss" / sekunder → ms. Tom streng → null. Ugyldig → undefined. */
export function parseElapsed(text: string): number | null | undefined {
  const t = text.trim().replace(/^\+/, '');
  if (t === '') return null;
  const neg = t.startsWith('-');
  const body = neg ? t.slice(1) : t;
  if (!/^[0-9:]+$/.test(body)) return undefined;
  const parts = body.split(':').map((p) => Number(p));
  if (parts.some((n) => Number.isNaN(n))) return undefined;
  let sec = 0;
  if (parts.length === 1) sec = parts[0]!;
  else if (parts.length === 2) sec = parts[0]! * 60 + parts[1]!;
  else if (parts.length === 3) sec = parts[0]! * 3600 + parts[1]! * 60 + parts[2]!;
  else return undefined;
  return (neg ? -1 : 1) * sec * 1000;
}

/**
 * Felt som speiler server-verdien, men som IKKE overskrives mens brukeren
 * skriver (polling hvert 4. sek skal ikke klobbe redigering).
 */
function useSyncedField(serverValue: string) {
  const [val, setVal] = useState(serverValue);
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setVal(serverValue);
  }, [serverValue]);
  return {
    val,
    setVal,
    onFocus: () => {
      focused.current = true;
    },
    onBlur: () => {
      focused.current = false;
    },
  };
}

export function FinishTiming({
  slug,
  startTimeMs,
  offsetMs,
  sail,
  password,
}: {
  slug: string;
  startTimeMs: number;
  offsetMs: number;
  sail: string;
  password: string;
}) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['finish', slug],
    queryFn: () => api.finishList(slug),
    refetchInterval: 4000,
  });

  const [pwError, setPwError] = useState<string | null>(null);
  const [unlocked, setUnlocked] = useState(false);
  const [newSail, setNewSail] = useState('');
  const newSailRef = useRef<HTMLInputElement>(null);

  const editable = list.data?.editable ?? false;
  const records = list.data?.records ?? [];
  const recordedCount = records.filter((r) => r.position !== null).length;
  const loggedIn = sail.trim().length > 0 && password.length > 0;

  const refresh = () => qc.invalidateQueries({ queryKey: ['finish', slug] });

  function handleWriteError(e: unknown) {
    const err = e as ApiError;
    if (err?.status === 401 || err?.status === 403) {
      setUnlocked(false);
      setPwError(err?.message ?? 'Du må være logget inn med en påmeldt båt.');
    } else {
      setPwError(err?.message ?? 'Noe gikk galt');
    }
  }

  async function unlock() {
    setPwError(null);
    try {
      await api.finishSeed(slug, sail, password); // fyll lista med påmeldte båter
      setUnlocked(true);
      refresh();
    } catch (e) {
      handleWriteError(e);
    }
  }

  const seedMore = useMutation({
    mutationFn: () => api.finishSeed(slug, sail, password),
    onSuccess: () => {
      setPwError(null);
      refresh();
    },
    onError: handleWriteError,
  });

  const addRow = useMutation({
    mutationFn: (s: string) => api.finishCreate(slug, sail, password, { sailNumber: s || null }),
    onSuccess: () => {
      setNewSail('');
      setPwError(null);
      refresh();
      newSailRef.current?.focus();
    },
    onError: handleWriteError,
  });

  // «Mål uten seilnr.» — sjelden, men nyttig for en uventet båt.
  const captureUnlisted = useMutation({
    mutationFn: () => {
      const elapsedMs = Math.round(syncedNow(offsetMs) - startTimeMs);
      return api.finishCreate(slug, sail, password, { elapsedMs, assignNextPosition: true });
    },
    onSuccess: refresh,
    onError: handleWriteError,
  });

  const canEdit = unlocked && editable && loggedIn;

  return (
    <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 mb-4">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-semibold">⏱ Tidtaking / målgang</h2>
        <span className="text-xs text-slate-500">
          {recordedCount} i mål / {records.length} i lista
        </span>
      </div>

      {!editable && (
        <p className="text-xs text-amber-400 mb-3">
          Regattaen er avsluttet. Lista er låst og vises som den var ved slutt.
        </p>
      )}

      {/* Åpne registrering — for påmeldte båter (innlogget med båt-passord) */}
      {editable && !canEdit && (
        <div className="mb-3">
          {!loggedIn ? (
            <p className="text-xs text-slate-400">
              Logg inn med båtnummer og båt-passord under «🔑 Logg inn med båt» på forsiden for å
              registrere tider.
            </p>
          ) : (
            <button
              onClick={unlock}
              className="w-full bg-sky-600 hover:bg-sky-500 active:bg-sky-700 rounded-lg py-2.5 text-sm font-semibold"
            >
              🔓 Åpne for tid- og resultatregistrering
            </button>
          )}
          {pwError && <div className="text-xs text-red-300 mt-1">{pwError}</div>}
        </div>
      )}

      {/* Legg til båt + verktøy når opplåst */}
      {canEdit && (
        <div className="mb-3 space-y-2">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (newSail.trim()) addRow.mutate(newSail.trim());
            }}
            className="flex gap-2"
          >
            <input
              ref={newSailRef}
              value={newSail}
              onChange={(e) => setNewSail(e.target.value.toUpperCase())}
              placeholder="Seilnummer — legg til båt i lista"
              autoCapitalize="characters"
              autoComplete="off"
              className="flex-1 px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm font-mono"
            />
            <button
              type="submit"
              disabled={!newSail.trim() || addRow.isPending}
              className="px-4 py-2 bg-sky-600 hover:bg-sky-500 rounded-lg text-sm font-semibold disabled:opacity-50"
            >
              ＋ Legg til
            </button>
          </form>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => seedMore.mutate()}
              className="text-xs text-slate-400 hover:text-sky-300"
              title="Legg til påmeldte båter som mangler i lista"
            >
              ↻ Hent påmeldte
            </button>
            <button
              onClick={() => captureUnlisted.mutate()}
              className="text-xs text-slate-400 hover:text-emerald-300"
              title="Lag en målført rad uten seilnummer (fyll inn etterpå)"
            >
              ＋ Mål uten seilnr.
            </button>
            <button
              onClick={() => setUnlocked(false)}
              className="ml-auto text-xs text-slate-500 hover:text-slate-300"
              title="Lukk registrering (lista vises fortsatt)"
            >
              Lukk
            </button>
          </div>
          {pwError && <div className="text-xs text-red-300">{pwError}</div>}
        </div>
      )}

      {/* Liste */}
      {records.length === 0 ? (
        <p className="text-sm text-slate-500">
          {canEdit
            ? 'Ingen båter i lista enda — legg til seilnummer over.'
            : 'Ingen registrerte tider enda.'}
        </p>
      ) : canEdit ? (
        <div className="rounded-lg border border-slate-800 divide-y divide-slate-800">
          <div className="flex items-center gap-2 px-2 py-1.5 bg-slate-900/60 text-xs text-slate-400">
            <span className="w-11 text-center">Plass</span>
            <span className="flex-1">Seilnr. / skipper</span>
            <span className="w-16">Tid</span>
          </div>
          {records.map((r) => (
            <EditableRow
              key={r.id}
              slug={slug}
              sail={sail}
              password={password}
              record={r}
              startTimeMs={startTimeMs}
              offsetMs={offsetMs}
              onChanged={refresh}
              onError={handleWriteError}
            />
          ))}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-800">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-900/60 text-slate-400 text-xs">
                <th className="text-left font-medium px-2 py-2 w-12">Plass</th>
                <th className="text-left font-medium px-2 py-2">Seilnr. / skipper</th>
                <th className="text-left font-medium px-2 py-2 w-20">Tid</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <ReadonlyRow key={r.id} record={r} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ReadonlyRow({ record }: { record: FinishRecord }) {
  return (
    <tr className="border-t border-slate-800">
      <td className="px-2 py-2 font-mono text-slate-400">{record.position ?? '—'}</td>
      <td className="px-2 py-2">
        <div className="font-mono">
          {record.sailNumber || <span className="text-slate-600 italic">—</span>}
          {record.note && <span className="ml-2 text-xs text-amber-300">{record.note}</span>}
        </div>
        {record.skipperName && (
          <div className="text-xs text-slate-400 truncate">{record.skipperName}</div>
        )}
      </td>
      <td className="px-2 py-2 font-mono text-slate-300 tabular-nums">
        {formatElapsed(record.elapsedMs)}
      </td>
    </tr>
  );
}

function EditableRow({
  slug,
  sail,
  password,
  record,
  startTimeMs,
  offsetMs,
  onChanged,
  onError,
}: {
  slug: string;
  sail: string;
  password: string;
  record: FinishRecord;
  startTimeMs: number;
  offsetMs: number;
  onChanged: () => void;
  onError: (e: unknown) => void;
}) {
  const sailField = useSyncedField(record.sailNumber ?? '');
  const pos = useSyncedField(record.position?.toString() ?? '');
  const time = useSyncedField(formatElapsed(record.elapsedMs));
  const [busy, setBusy] = useState(false);
  const [savedTick, setSavedTick] = useState(false);
  const [timeBad, setTimeBad] = useState(false);

  const recorded = record.position !== null;

  async function patch(p: {
    sailNumber?: string | null;
    position?: number | null;
    elapsedMs?: number | null;
    note?: string | null;
  }) {
    setBusy(true);
    try {
      await api.finishUpdate(slug, record.id, sail, password, p);
      setSavedTick(true);
      setTimeout(() => setSavedTick(false), 1200);
      onChanged();
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  function commitSail() {
    sailField.onBlur();
    const next = sailField.val.trim();
    if (next === (record.sailNumber ?? '')) return;
    void patch({ sailNumber: next || null });
  }
  function commitPos() {
    pos.onBlur();
    const trimmed = pos.val.trim();
    const next = trimmed === '' ? null : parseInt(trimmed, 10);
    if (next !== null && Number.isNaN(next)) return;
    if (next === (record.position ?? null)) return;
    void patch({ position: next });
  }
  function commitTime() {
    time.onBlur();
    const parsed = parseElapsed(time.val);
    if (parsed === undefined) {
      setTimeBad(true);
      return;
    }
    setTimeBad(false);
    if (parsed === (record.elapsedMs ?? null)) return;
    void patch({ elapsedMs: parsed });
  }

  async function recordFinish() {
    setBusy(true);
    try {
      const elapsedMs = Math.round(syncedNow(offsetMs) - startTimeMs);
      await api.finishRecord(slug, record.id, sail, password, elapsedMs);
      onChanged();
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  async function recordGps() {
    if (record.autoElapsedMs == null) return;
    setBusy(true);
    try {
      await api.finishRecord(slug, record.id, sail, password, record.autoElapsedMs);
      onChanged();
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  const del = useMutation({
    mutationFn: () => api.finishDelete(slug, record.id, sail, password),
    onSuccess: onChanged,
    onError,
  });

  // Hurtig-status. Trykk på aktiv kode for å fjerne den.
  const STATUSES = ['DNF', 'DNS', 'DSQ', 'RET'];
  function toggleStatus(code: string) {
    void patch({ note: record.note === code ? null : code });
  }
  const customNote = record.note && !STATUSES.includes(record.note) ? record.note : null;

  return (
    <div className={`px-2 py-2 ${recorded ? 'bg-emerald-950/20' : ''}`}>
      <div className="flex items-center gap-2">
        <input
          inputMode="numeric"
          value={pos.val}
          onChange={(e) => pos.setVal(e.target.value)}
          onFocus={pos.onFocus}
          onBlur={commitPos}
          className="w-11 px-1 py-1.5 bg-slate-950 border border-slate-700 rounded text-center font-mono text-sm"
        />
        <input
          value={sailField.val}
          onChange={(e) => sailField.setVal(e.target.value.toUpperCase())}
          onFocus={sailField.onFocus}
          onBlur={commitSail}
          placeholder="seilnr."
          autoCapitalize="characters"
          className="flex-1 min-w-0 px-2 py-1.5 bg-slate-950 border border-slate-700 rounded font-mono text-sm"
        />
        <input
          value={time.val}
          onChange={(e) => time.setVal(e.target.value)}
          onFocus={time.onFocus}
          onBlur={commitTime}
          placeholder="m:ss"
          inputMode="numeric"
          className={`w-16 px-1.5 py-1.5 bg-slate-950 border rounded font-mono tabular-nums text-sm ${
            timeBad ? 'border-red-600' : 'border-slate-700'
          }`}
        />
        {!recorded ? (
          <button
            onClick={recordFinish}
            disabled={busy}
            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 rounded text-xs font-bold disabled:opacity-50 whitespace-nowrap"
            title="Registrer mål: neste plass + tid fra klokka"
          >
            🏁 Mål
          </button>
        ) : (
          <button
            onClick={() => void patch({ position: null, elapsedMs: null })}
            disabled={busy}
            className="px-2 py-1.5 text-slate-400 hover:text-slate-200 text-sm"
            title="Angre målføring (nullstill plass og tid)"
          >
            ↩
          </button>
        )}
        {savedTick ? (
          <span className="text-xs text-emerald-400 w-4 text-center">✓</span>
        ) : (
          <button
            onClick={() => {
              if (confirm('Slette denne raden?')) del.mutate();
            }}
            className="text-red-400 hover:text-red-300 text-sm w-4"
            title="Slett rad"
          >
            ✕
          </button>
        )}
      </div>
      {/* Skipper + statuskoder på samme linje for å spare plass */}
      <div className="flex items-center gap-1 mt-1.5">
        {!recorded && record.autoElapsedMs != null && (
          <button
            onClick={recordGps}
            disabled={busy}
            className="shrink-0 px-1.5 py-0.5 rounded text-[11px] font-bold bg-sky-700 hover:bg-sky-600 text-white disabled:opacity-50"
            title="Registrer mål med GPS-tid (kryssing av mållinja)"
          >
            🛰 {formatElapsed(record.autoElapsedMs)}
          </button>
        )}
        <span
          className="text-xs text-slate-400 truncate flex-1 min-w-0"
          title={record.skipperName ?? undefined}
        >
          {record.skipperName ? `👤 ${record.skipperName}` : ''}
        </span>
        {STATUSES.map((code) => (
          <button
            key={code}
            onClick={() => toggleStatus(code)}
            disabled={busy}
            className={`shrink-0 px-1.5 py-0.5 rounded text-[11px] font-medium disabled:opacity-50 ${
              record.note === code
                ? 'bg-amber-600 text-white'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
            title={statusTitle(code)}
          >
            {code}
          </button>
        ))}
        {customNote && <span className="text-xs text-amber-300 ml-1 shrink-0">{customNote}</span>}
      </div>
    </div>
  );
}

function statusTitle(code: string): string {
  switch (code) {
    case 'DNF':
      return 'Did Not Finish — startet, men fullførte ikke';
    case 'DNS':
      return 'Did Not Start — møtte ikke til start';
    case 'DSQ':
      return 'Disqualified — diskvalifisert';
    case 'RET':
      return 'Retired — trakk seg';
    default:
      return code;
  }
}
